import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Star, MapPin, Users, Lightning, Heart, Flower, Crown } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const TRIBUTE_EMOJIS = ['🌹', '🕯️', '💫', '⭐', '🌟', '💜', '🪦', '🪷'];

const PREMIUM_TIERS = [
  { key: 'flower', label: 'Flower', emoji: '🌹', price: 1, accent: 'border-bx-magenta/40 text-bx-magenta' },
  { key: 'candle', label: 'Candle', emoji: '🕯️', price: 3, accent: 'border-bx-gold/40 text-bx-gold' },
  { key: 'star',   label: 'Memorial Star', emoji: '🌟', price: 20, accent: 'border-bx-volt/40 text-bx-volt' },
];

const Memorial = () => {
  const { id } = useParams();
  const loc = useLocation();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState('');

  const load = () => api.get(`/memorials/${id}`).then(({ data }) => setM(data.memorial));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // poll tribute payment status if returning from Stripe
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const sess = q.get('tribute_session');
    if (!sess) return;
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const { data } = await api.get(`/tributes/status/${sess}`);
        if (data.fulfilled) {
          toast.success('Your tribute is now part of the memorial 🕯️');
          window.history.replaceState({}, '', loc.pathname);
          load();
          return;
        }
      } catch {}
      if (tries < 10) setTimeout(tick, 1500);
    };
    tick();
  }, [loc.search]); // eslint-disable-line

  const leaveTribute = async (emoji) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/memorials/${id}/tribute`, { emoji });
      setM((prev) => ({ ...prev, tributes: data.tributes, tribute_emojis: data.tribute_emojis }));
      toast.success(`${emoji} placed in remembrance`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'tribute failed');
    } finally { setBusy(false); }
  };

  const premiumTribute = async (tier) => {
    setPaying(tier);
    try {
      const { data } = await api.post('/tributes/checkout', {
        memorial_id: id, tier, origin_url: window.location.href.split('?')[0],
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'checkout failed');
      setPaying('');
    }
  };

  if (!m) return <div className="text-center py-20 text-white/65">loading memorial…</div>;

  const days = Math.max(0, Math.round(
    (new Date(m.died_at).getTime() - new Date(m.born_at).getTime()) / 86400000
  ));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center">
        <div className="chip mx-auto">memorial · burst into stars</div>
        <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter mt-3">
          <span className="shimmer-text">{m.name}</span>
        </h1>
        <div className="mt-2 text-sm text-white/60">
          <span className={`rar-${m.rarity}`}>{m.rarity}</span> · {m.bubble_class}-class · {days} days alive
        </div>

        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="my-10 flex justify-center">
          <div className="relative h-72 w-72">
            <div className={`absolute inset-0 rounded-full blur-3xl opacity-30 bg-rar-${m.rarity}`} />
            {bubbleImageSrc(m) ? (
              <img src={bubbleImageSrc(m)} alt={m.name}
                   style={{ filter: 'grayscale(0.5) brightness(0.85)' }}
                   className="relative h-full w-full rounded-full object-cover ring-2 ring-white/10 breathe" />
            ) : (
              <div className={`relative h-full w-full rounded-full bg-rar-${m.rarity}`} />
            )}
          </div>
        </motion.div>

        {m.last_words && (
          <div className="glass rounded-2xl p-6 max-w-lg mx-auto">
            <div className="text-[10px] uppercase tracking-widest text-white/60">final words</div>
            <p className="font-display text-xl mt-2 italic">"{m.last_words}"</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
          <Stat icon={Users} label="Unique holders" value={m.unique_holders} color="text-bx-cyan" />
          <Stat icon={Lightning} label="Transfers" value={m.transfers} color="text-bx-volt" />
          <Stat icon={MapPin} label="Days alive" value={days} color="text-bx-magenta" />
          <Stat icon={Star} label="Secret" value={m.secret_personality || '—'} color="text-bx-gold" />
        </div>

        {m.personality_bio && (
          <p className="text-white/65 mt-6 italic">"{m.personality_bio}"</p>
        )}

        <div className="mt-12 text-xs text-white/60 flex items-center justify-center gap-2">
          <Heart size={12} weight="fill" className="text-bx-magenta" />
          Born {new Date(m.born_at).toLocaleDateString()} · passed on {new Date(m.died_at).toLocaleDateString()}
        </div>

        {/* Bloodline */}
        {m.child_name && (
          <div className="grad-border p-5 mt-8 max-w-lg mx-auto">
            <div className="text-[10px] uppercase tracking-widest text-bx-volt flex items-center justify-center gap-1">
              <Crown size={12} weight="fill" /> living legacy
            </div>
            <div className="text-sm text-white/80 mt-2">
              Their pulse continues in <Link to={`/b/${m.child_id}`} className="text-bx-cyan font-bold">{m.child_name}</Link>.
            </div>
          </div>
        )}
        {m.parent_name && (
          <div className="grad-border p-5 mt-4 max-w-lg mx-auto">
            <div className="text-[10px] uppercase tracking-widest text-bx-gold flex items-center justify-center gap-1">
              <Crown size={12} weight="fill" /> bloodline
            </div>
            <div className="text-sm text-white/80 mt-2">
              Born from <Link to={`/b/${m.parent_id}`} className="text-bx-cyan font-bold">{m.parent_name}</Link>.
            </div>
          </div>
        )}

        {/* Tributes */}
        <div className="mt-10 max-w-lg mx-auto">
          <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-white/60 mb-3">
            <Flower size={12} weight="fill" className="text-bx-magenta" />
            tributes · {m.tributes || 0} left in remembrance
          </div>
          <div className="glass rounded-2xl p-5">
            {Object.keys(m.tribute_emojis || {}).length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
                {Object.entries(m.tribute_emojis || {}).map(([emo, n]) => (
                  <div key={emo} className="px-3 py-1.5 rounded-full glass-hi text-sm flex items-center gap-1">
                    <span>{emo}</span><span className="text-white/60 text-xs">{n}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {TRIBUTE_EMOJIS.map((e) => (
                <button
                  key={e}
                  data-testid={`tribute-${e}`}
                  disabled={busy}
                  onClick={() => leaveTribute(e)}
                  className="text-2xl h-12 w-12 rounded-full glass-hi hover:bg-white/10 transition disabled:opacity-50"
                >{e}</button>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-white/55 text-center mt-3">
              tap to leave one
            </div>

            <div className="mt-5 pt-5 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-widest text-bx-gold text-center mb-3">
                eternal tributes · raised so far ${(m.tributes_paid_usd || 0).toFixed(2)}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {PREMIUM_TIERS.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`premium-tribute-${t.key}`}
                    disabled={!!paying}
                    onClick={() => premiumTribute(t.key)}
                    className={`p-3 rounded-xl border glass-hi text-center hover:bg-white/10 transition disabled:opacity-50 ${t.accent}`}
                  >
                    <div className="text-2xl">{t.emoji}</div>
                    <div className="text-xs font-bold mt-1">{t.label}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">${t.price}</div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/55 text-center mt-3">
                stays on this grave forever
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Link to="/hall-of-fame" data-testid="memorial-back-hall" className="btn-primary px-6 py-3 rounded-full text-sm">
            Back to the Hall
          </Link>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="glass-hi rounded-xl p-4">
    <Icon size={16} weight="duotone" className={color} />
    <div className={`font-display text-xl font-black mt-1 ${color}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-white/60 mt-0.5">{label}</div>
  </div>
);

export default Memorial;
